import { Settings } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FONT_OPTIONS,
  PAGE_SIZES,
  SECTION_DEFS,
  SECTION_ORDER,
} from "@/lib/elements";
import {
  PropGroup,
  PropRow,
  PropInput,
  PropSelect,
} from "./primitives";

export function PageSettingsEditor({ settings, onUpdate }) {
  const sp = settings.sectionPadding || {};
  const updateSp = (key, field, val) => {
    onUpdate({ ...settings, sectionPadding: { ...sp, [key]: { ...(sp[key] || {}), [field]: val } } });
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Settings className="w-4 h-4 text-gray-400" />
        <p className="text-sm font-heading font-bold text-gray-700">Page Settings</p>
      </div>

      <PropGroup title="Page Size">
        <PropRow label="Size">
          <Select value={settings.pageSize || "A4"} onValueChange={(v) => onUpdate({ ...settings, pageSize: v })}>
            <SelectTrigger className="h-7 text-xs" data-testid="page-size-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PAGE_SIZES).map(([key, def]) => (
                <SelectItem key={key} value={key} className="text-xs" data-testid={`page-size-${key}`}>
                  {def.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PropRow>
        {settings.pageSize === "Custom" && (
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
            <PropInput
              label="Width (mm)"
              type="number"
              value={settings.customWidthMM ?? 210}
              onChange={(v) => onUpdate({ ...settings, customWidthMM: v })}
              min={50}
              max={2000}
            />
            <PropInput
              label="Height (mm)"
              type="number"
              value={settings.customHeightMM ?? 297}
              onChange={(v) => onUpdate({ ...settings, customHeightMM: v })}
              min={50}
              max={2000}
            />
          </div>
        )}
        <PropRow label="Orientation">
          <div className="flex gap-1 w-full" data-testid="orientation-toggle">
            {[
              { v: "portrait",  label: "Portrait" },
              { v: "landscape", label: "Landscape" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`orientation-${o.v}`}
                onClick={() => onUpdate({ ...settings, orientation: o.v })}
                className={`flex-1 h-7 text-[11px] rounded border transition ${
                  (settings.orientation || "portrait") === o.v
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </PropRow>
      </PropGroup>

      <Separator />

      <PropGroup title="Margins (mm)">
        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
          <PropInput label="Top"    type="number" value={settings.marginTop}    onChange={(v) => onUpdate({ ...settings, marginTop: v })} />
          <PropInput label="Bottom" type="number" value={settings.marginBottom} onChange={(v) => onUpdate({ ...settings, marginBottom: v })} />
          <PropInput label="Left"   type="number" value={settings.marginLeft}   onChange={(v) => onUpdate({ ...settings, marginLeft: v })} />
          <PropInput label="Right"  type="number" value={settings.marginRight}  onChange={(v) => onUpdate({ ...settings, marginRight: v })} />
        </div>
      </PropGroup>

      <Separator />

      <PropGroup title="Typography">
        <PropSelect label="Font"      value={settings.fontFamily} onChange={(v) => onUpdate({ ...settings, fontFamily: v })} options={FONT_OPTIONS} />
        <PropInput  label="Base Size" type="number" value={settings.fontSize}   onChange={(v) => onUpdate({ ...settings, fontSize: v })} min={8} max={20} />
      </PropGroup>

      <Separator />

      <PropGroup title="Section Padding (px)">
        <p className="text-[9px] text-gray-400 mb-2">Configure top/bottom padding for each section</p>
        {SECTION_ORDER.map((key) => {
          const def = SECTION_DEFS[key];
          const sec = sp[key] || { top: 0, bottom: 0 };
          return (
            <div key={key} className="space-y-1">
              <p className="text-[10px] font-semibold" style={{ color: def.color }}>{def.label}</p>
              <div className="grid grid-cols-2 gap-x-2">
                <PropInput label="Top"    type="number" value={sec.top    || 0} onChange={(v) => updateSp(key, "top", v)}    min={0} max={100} />
                <PropInput label="Bottom" type="number" value={sec.bottom || 0} onChange={(v) => updateSp(key, "bottom", v)} min={0} max={100} />
              </div>
            </div>
          );
        })}
      </PropGroup>
    </div>
  );
}
